import type { ChangeEvent, ReactNode } from 'react';
import { ErrorMessage, Field, getIn, useFormikContext } from 'formik';
import InputErrorMessage from '../shared/InputErrorMessage';
import type { FormikTouchedErrorsForm } from '../shared/formikTypes';
import { getFieldErrorState } from '../shared/formFieldUi';
import Check from '../../../assets/icons/check.svg';
import s from './CheckboxInput.module.scss';

interface CheckboxInputProps {
  name: string;
  labelContent: ReactNode;
  required?: boolean;
  disabled?: boolean;
}

function isCheckboxChecked(value: unknown): boolean {
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  return Boolean(value);
}

function CheckboxInput({
  name,
  labelContent,
  required = false,
  disabled = false,
}: CheckboxInputProps) {
  const form = useFormikContext();
  const value = getIn(form.values, name);
  const checked = isCheckboxChecked(value);
  const { isError, errorElementId, ariaDescribedBy } = getFieldErrorState(
    form as FormikTouchedErrorsForm,
    name
  );

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    void form.setFieldValue(name, event.target.checked);
  };

  const handleBlur = () => {
    void form.setFieldTouched(name, true);
  };

  return (
    <div className={s.checkboxWrapper}>
      <label htmlFor={name}>
        <Field
          type="checkbox"
          name={name}
          id={name}
          checked={checked}
          onChange={handleChange}
          onBlur={handleBlur}
          required={required}
          disabled={disabled}
          aria-invalid={isError}
          aria-describedby={ariaDescribedBy}
        />
        <div className={s.checkmark}>{checked && <img src={Check} alt="" />}</div>
        <div>{labelContent}</div>
      </label>
      <div id={errorElementId}>
        <ErrorMessage name={name} component={InputErrorMessage} />
      </div>
    </div>
  );
}

export default CheckboxInput;
